import { useContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import {
  getVoiceStudyCard,
  useVoiceStudyEnabledIfProvided,
} from "@-/dev.improve.haniverse.lib.voice-study";
import {
  morphemeMnemonicsPanelMorphemeSliceQueryFn,
  useGetMorphemeMnemonicsPanelMorphemeSliceQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import {
  decompositionMapQueryFn,
  useGetDecompositionMapQueryKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.decomposition";
import {
  CARD_CONTENT_OVERLAY_TABLES,
  cardContentDrillType,
  cardContentIdentityKey,
  cardContentSubject,
  cardIdentityKey,
  cardReviewSettingsFromUser,
  resolveAppCardContent,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import { useReadUserCardAppCards } from "@-/dev.improve.haniverse.lib.cards.app-card";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { orderReviewQueue, stampExposure } from "#order-review-queue";
import {
  type DueUserCard,
  dueUserCardsQueryFn,
  filterReviewEligibleDueUserCards,
  type ReviewEligibleDueUserCard,
  useDueUserCardsQueryOptions,
  useGetDueUserCardsQueryKey,
} from "#use-due-user-cards-query";
import { useRecentExposuresQueryOptions } from "#use-recent-exposures-query";
import type { QueryClient } from "@-/com.npmjs.tanstack__react-query";
import {
  type PinnedReviewQueueCard,
  pendingPinnedReviewCards,
  usePinnedReviewQueueQueryOptions,
} from "#use-pinned-review-queue-query";

const NEXT_DUE_USER_CARD_DATA_TABLES = [
  "user_card",
  "fsrs_card",
  ...CARD_CONTENT_OVERLAY_TABLES,
  // The pinyin-flashcards setting (now on the `user` row) gates whether pronunciation
  // cards are eligible for review, so a toggle of it must re-run this query.
  "user",
  // A committed rating is what makes its related cards "hot" (see `orderReviewQueue`), so the
  // serving order changes with every review row.
  "user_card_review",
] as const;

/** A due card the deck drills: a grapheme or word card, by its drill identity. */
export type NextDueDrillCardData = {
  drillType: DrillType;
  due: string;
  grapheme: string;
  kind: "drill";
  /**
   * `null` for the meaning card of a character whose lesson is still ahead in the deck's queue
   * (`introducingGraphemes`): accepting that lesson enrolls it before it is shown.
   */
  userCardId: string | null;
};

/** A due quiz card, by its quiz's catalogue key: answered, not drilled. */
export type NextDueQuizCardData = {
  due: string;
  kind: "quiz";
  quizKey: string;
  userCardId: string;
};

export type NextDueUserCardData = NextDueDrillCardData | NextDueQuizCardData;

export type NextDueUserCardDataOverride = {
  /**
   * The subject of the card the next one is planned after. Its review may not have landed yet when
   * the deck plans its flashcard queue, so the scheduler treats it as reviewed at fetch time: the same
   * grapheme's sibling card, its compounds and a re-review of it are pushed behind every other
   * ready card, and served only when nothing else is due.
   */
  deprioritizeGrapheme?: string;
  /**
   * Characters whose lessons are ahead of the planned card and not accepted yet, oldest first. When
   * accepting them spends today's new-character allowance, the plan is made as if it were already
   * spent: recently learned cards are revived (the due scan's introduction hold lifts), and these
   * characters' own meaning cards follow, after every enrolled card, as reviews.
   */
  introducingGraphemes?: readonly string[];
  /** Already in the deck's flashcard queue; never exclude pronunciation siblings. */
  predictionCards?: readonly { drillType: DrillType; grapheme: string }[];
  /** Subjects queued before the planned flashcard, without writing exposure records. */
  predictionExposures?: readonly string[];
  /** Quizzes (catalogue keys) already in the deck's flashcard queue. */
  predictionQuizKeys?: readonly string[];
  voiceOnly?: boolean;
};

const EMPTY_OVERRIDE: NextDueUserCardDataOverride = {};

/**
 * How many due candidates to resolve against the content catalog per round while looking for the
 * first servable one. Almost always the first candidate resolves, so one round is one batched
 * actor read + one content read; a handful at a time keeps a run of unresolvable cards (overlays
 * whose content row is missing from this build) from costing a round-trip each.
 */
const CANDIDATE_RESOLVE_BATCH_SIZE = 8;

/**
 * Review-routing query over the due-card scan, for reactive consumers (the next-card prefetch
 * hooks, the lesson next-button, the learn summary). Imperative post-rating navigation calls the
 * resolver directly — see `resolveLearnCardDestination` — so a cached entry here can never decide a navigation.
 *
 * Key composition: the due scan (`useDueUserCardsQueryOptions`) is shared with the due-count badge
 * and the summary grid, so all three refetch together off one cache entry; the user row carries
 * the review-eligibility settings; the recent-exposures read and the static decomposition map feed
 * the serving order (`orderReviewQueue`). Candidate content is temporary: rejected cards never add
 * per-card or content-batch entries to the query cache.
 */
export function useGetNextDueUserCardDataQueryKey() {
  // Also built outside the Learn stack (the web header and sign-in prefetch the summary), where no
  // voice session can exist.
  const voiceEnabled = useVoiceStudyEnabledIfProvided();
  const [studyEnvironment] = useDeviceSetting("studyEnvironment");
  const [reviewFocus] = useDeviceSetting("reviewFocus");
  // Offline mode's catalog holds only the free tier, which decides which due cards are servable.
  const contentAvailability = useContentAvailability();
  const getMnemonicKey = useGetMorphemeMnemonicsPanelMorphemeSliceQueryKey();
  const userQueryOptions = actorQueries(import.meta.location).user.findFirst.useQueryOptions({});
  const dueUserCardsQueryOptions = useDueUserCardsQueryOptions();
  const getDueUserCardsQueryKey = useGetDueUserCardsQueryKey();
  const dailyNewGraphemeAllowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();
  const recentExposuresQueryOptions = useRecentExposuresQueryOptions();
  const pinnedReviewQueueQueryOptions = usePinnedReviewQueueQueryOptions();
  const getDecompositionMapQueryKey = useGetDecompositionMapQueryKey();
  const readUserCardAppCards = useReadUserCardAppCards();

  // Registered against its own tables even though every nested read registers too: the composed
  // entry is what the observers hold, and an unregistered composed key would be served from cache
  // forever (`staleTime` is `Infinity` for untracked keys) after the nested entries went stale.
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(NEXT_DUE_USER_CARD_DATA_TABLES),
  );

  return (override: NextDueUserCardDataOverride = EMPTY_OVERRIDE) =>
    withTableChangeVersion([
      "nextDueUserCardData",
      override.deprioritizeGrapheme,
      {
        contentAvailability,
        dailyNewGraphemeAllowanceQueryOptions,
        dueUserCardsQueryOptions,
        getDecompositionMapQueryKey,
        getDueUserCardsQueryKey,
        getMnemonicKey,
        introducingGraphemes: override.introducingGraphemes,
        pinnedReviewQueueQueryOptions,
        predictionCards: override.predictionCards,
        predictionExposures: override.predictionExposures,
        predictionQuizKeys: override.predictionQuizKeys,
        readUserCardAppCards,
        recentExposuresQueryOptions,
        reviewFocus,
        studiedGraphemesQueryOptions,
        studyEnvironment,
        userQueryOptions,
        voiceOnly: override.voiceOnly ?? voiceEnabled,
      },
    ] as const);
}

type NextDueUserCardDataQueryKey = ReturnType<ReturnType<typeof useGetNextDueUserCardDataQueryKey>>;

/** The review queue in serving order, before content resolution picks the first servable card. */
export type ReviewQueue = {
  /**
   * Characters whose lessons are ahead (`introducingGraphemes`) and spend today's allowance: their
   * meaning cards, not enrolled yet, follow every other card as reviews. Eligible ones only.
   */
  introducedGraphemes: string[];
  /** The computed order (`orderReviewQueue`) of the remaining eligible due cards. */
  orderedDueCards: ReviewEligibleDueUserCard[];
  /** The hardcoded head: unspent pinned cards that pass the eligibility gates, in stored order. */
  pinnedCards: ReviewEligibleDueUserCard[];
  /** Every pinned entry, spent and ineligible ones included, for the Admin panel's editor. */
  pinnedReviewQueue: PinnedReviewQueueCard[];
};

/**
 * Build the review queue the next-due read serves from: the hardcoded head, then every other
 * eligible due card in the computed order.
 */
async function readReviewQueue({
  client,
  queryKey: [
    _,
    deprioritizeGrapheme,
    {
      contentAvailability,
      dailyNewGraphemeAllowanceQueryOptions,
      dueUserCardsQueryOptions,
      getDecompositionMapQueryKey,
      getDueUserCardsQueryKey,
      introducingGraphemes,
      pinnedReviewQueueQueryOptions,
      predictionExposures,
      recentExposuresQueryOptions,
      reviewFocus,
      studiedGraphemesQueryOptions,
      studyEnvironment,
      userQueryOptions,
    },
  ],
}: {
  client: QueryClient;
  queryKey: NextDueUserCardDataQueryKey;
}): Promise<ReviewQueue> {
  const [userSettings, pinnedReviewQueue, spendingIntroductions] = await Promise.all([
    client.fetchQuery(userQueryOptions),
    client.fetchQuery(pinnedReviewQueueQueryOptions),
    readAllowanceSpendingIntroductions({
      client,
      dailyNewGraphemeAllowanceQueryOptions,
      introducingGraphemes,
      studiedGraphemesQueryOptions,
    }),
  ]);
  // Lessons ahead that spend today's allowance lift the hold before their acceptance lands; the
  // shared scan lifts it by itself once it has.
  const dueUserCards = await client.fetchQuery(
    spendingIntroductions.length === 0
      ? dueUserCardsQueryOptions
      : {
          queryFn: dueUserCardsQueryFn,
          queryKey: getDueUserCardsQueryKey({ liftIntroductionHold: true }),
        },
  );
  const cardReviewSettings = cardReviewSettingsFromUser(userSettings);
  const queueSettings = { contentAvailability, reviewFocus, studyEnvironment };
  const now = Date.now();
  const introducedGraphemes = filterReviewEligibleDueUserCards(
    spendingIntroductions.map(
      (grapheme): DueUserCard => ({
        cardContentKind: "grapheme-meaning",
        due: new Date(now).toISOString(),
        dueMs: now,
        subject: { kind: "grapheme", text: grapheme },
        userCardId: `introducing:${grapheme}`,
      }),
    ),
    cardReviewSettings,
    queueSettings,
  ).map((card) => card.subject.text);

  // Pronunciation (pinyin flashcard) cards are always authored but only enter the review
  // rotation when the user has the synced "Pinyin flashcards" setting on, and the study
  // environment and review focus narrow that further (no mic drills during Silent Study, only
  // pronunciation cards while practicing pronunciation, …). Eligibility is decided from the actor
  // overlay's kind, which is the same kind the content row resolves to.
  //
  // The Admin panel's hardcoded queue (see `usePinnedReviewQueueQueryOptions`) heads the queue in
  // its stored order, due or not, under the same eligibility gates; the computed order follows.
  const pendingPinnedCards = pendingPinnedReviewCards(pinnedReviewQueue);
  const pinnedCards = filterReviewEligibleDueUserCards(
    pendingPinnedCards,
    cardReviewSettings,
    queueSettings,
  );
  const pendingPinnedCardIds = new Set(pendingPinnedCards.map((card) => card.userCardId));
  const eligibleDueUserCards = filterReviewEligibleDueUserCards(
    pendingPinnedCardIds.size === 0
      ? dueUserCards
      : dueUserCards.filter((card) => !pendingPinnedCardIds.has(card.userCardId)),
    cardReviewSettings,
    queueSettings,
  );

  // Ordering cannot change an empty/single-card queue. In particular a learner with no
  // reviews should never open the static decomposition catalogue just to find that out.
  if (eligibleDueUserCards.length <= 1) {
    return {
      introducedGraphemes,
      orderedDueCards: eligibleDueUserCards,
      pinnedCards,
      pinnedReviewQueue,
    };
  }
  const recentExposures = await client.fetchQuery(recentExposuresQueryOptions);
  const exposures = new Map(recentExposures);
  for (const grapheme of predictionExposures ?? []) {
    stampExposure(exposures, grapheme, now);
  }
  // Ordering inspects due meaning subjects and exposed subjects (including a virtual
  // preceding card). Their transitive closure is sufficient; unrelated catalogue rows
  // cannot change blocked depth or exposure priority.
  const roots = uniq(
    [
      ...eligibleDueUserCards.flatMap((card) =>
        card.cardContentKind === "grapheme-meaning" ? [card.subject.text] : [],
      ),
      ...exposures.keys(),
      ...(deprioritizeGrapheme === undefined ? [] : [deprioritizeGrapheme]),
      // oxlint-disable-next-line typescript/no-misused-spread -- Static grapheme keys and exposure stamping use Unicode code points, including supplementary Han characters.
    ].flatMap((subject) => [subject, ...subject]),
  ).sort();
  const decompositionMap = await client.fetchQuery({
    queryFn: decompositionMapQueryFn,
    queryKey: getDecompositionMapQueryKey(roots),
  });
  return {
    introducedGraphemes,
    orderedDueCards: orderReviewQueue({
      decompositionMap,
      dueCards: eligibleDueUserCards,
      now,
      recentExposures: exposures,
      virtualExposureSubject: deprioritizeGrapheme,
    }),
    pinnedCards,
    pinnedReviewQueue,
  };
}

export const [useNextDueUserCardDataQueryOptions, nextDueUserCardDataQueryFn] =
  createUseQueryOptions(useGetNextDueUserCardDataQueryKey, async (context) => {
    const {
      client,
      queryKey: [
        _,
        _deprioritizeGrapheme,
        { getMnemonicKey, predictionCards, predictionQuizKeys, readUserCardAppCards, voiceOnly },
      ],
    } = context;
    const { introducedGraphemes, orderedDueCards, pinnedCards } = await readReviewQueue({
      client,
      queryKey: context.queryKey,
    });
    if (
      pinnedCards.length === 0 &&
      orderedDueCards.length === 0 &&
      introducedGraphemes.length === 0
    ) {
      return null;
    }

    const isVoiceEligible = voiceOnly
      ? async (grapheme: string) =>
          getVoiceStudyCard(
            // Rejected voice candidates must not retain their full mnemonic/stroke slices.
            await morphemeMnemonicsPanelMorphemeSliceQueryFn({
              ...context,
              queryKey: getMnemonicKey(grapheme),
            }),
          ) !== null
      : undefined;

    const enrolledCard = await resolveFirstServableCard({
      candidates: pinnedCards.length === 0 ? orderedDueCards : [...pinnedCards, ...orderedDueCards],
      isVoiceEligible,
      predictionCards,
      predictionQuizKeys,
      readUserCardAppCards,
    });
    if (enrolledCard !== null) {
      return enrolledCard;
    }
    // Enrolled by the lesson ahead before it is shown, so its drill identity is all it needs now.
    for (const grapheme of introducedGraphemes) {
      const drillType: DrillType = { kind: "meaning" };
      if (
        predictionCards?.some(
          (card) =>
            cardIdentityKey({ drillType: card.drillType, subjectText: card.grapheme }) ===
            cardIdentityKey({ drillType, subjectText: grapheme }),
        ) ||
        (isVoiceEligible !== undefined && !(await isVoiceEligible(grapheme)))
      ) {
        continue;
      }
      return {
        drillType,
        due: new Date().toISOString(),
        grapheme,
        kind: "drill",
        userCardId: null,
      } satisfies NextDueDrillCardData;
    }
    return null;
  });

/**
 * Of the characters whose lessons are ahead of the planned card (`introducingGraphemes`), the ones
 * not enrolled yet, when accepting them spends today's new-character allowance: the plan then
 * revives what was just learned, as the deck will once they land. Empty otherwise, including when
 * the allowance is already spent (the shared scan lifts its hold by itself then).
 */
async function readAllowanceSpendingIntroductions({
  client,
  dailyNewGraphemeAllowanceQueryOptions,
  introducingGraphemes,
  studiedGraphemesQueryOptions,
}: {
  client: QueryClient;
  dailyNewGraphemeAllowanceQueryOptions: ReturnType<typeof useDailyNewGraphemeAllowanceQueryOptions>;
  introducingGraphemes: readonly string[] | undefined;
  studiedGraphemesQueryOptions: ReturnType<typeof useStudiedGraphemesQueryOptions>;
}): Promise<string[]> {
  if (introducingGraphemes === undefined || introducingGraphemes.length === 0) {
    return [];
  }
  const [allowance, studiedGraphemes] = await Promise.all([
    client.fetchQuery(dailyNewGraphemeAllowanceQueryOptions),
    client.fetchQuery(studiedGraphemesQueryOptions),
  ]);
  const pending = uniq([...introducingGraphemes]).filter(
    (grapheme) => !studiedGraphemes.has(grapheme),
  );
  return pending.length > 0 && !allowance.isLimitReached && allowance.remaining <= pending.length
    ? pending
    : [];
}

/**
 * The whole review queue for the Admin panel's editor: the hardcoded head, its spent and
 * ineligible entries, and the computed order behind it. Built from the next-due read's key with no
 * planning override, so it lists what the deck's flashcard queue is planned from (before the cards
 * already queued are passed over).
 */
export function useGetReviewQueueQueryKey() {
  const getNextDueUserCardDataQueryKey = useGetNextDueUserCardDataQueryKey();
  const withTableChangeVersion = useTableChangeVersion(
    tableNameMatcher(NEXT_DUE_USER_CARD_DATA_TABLES),
  );
  return () => withTableChangeVersion(["reviewQueue", getNextDueUserCardDataQueryKey()] as const);
}

export const [useReviewQueueQueryOptions, reviewQueueQueryFn] = createUseQueryOptions(
  useGetReviewQueueQueryKey,
  async ({ client, queryKey: [_, nextDueUserCardDataQueryKey] }): Promise<ReviewQueue> =>
    readReviewQueue({ client, queryKey: nextDueUserCardDataQueryKey }),
);

/**
 * The first candidate (in serving order) the scheduler serves.
 *
 * Every candidate already passed `filterReviewEligibleDueUserCards`, the same filter behind every
 * due count (the "N left" pill, the summary, the recharge bar). A `null` result is read by the
 * destination resolver as an exhausted queue, which opens new cards — so every candidate passed
 * over here is one the counts still show. Deliberate narrowings pass cards over: one the
 * speculative deck already holds, and one voice study cannot drill.
 *
 * A due card whose content row is absent from the ACTIVE content database is passed over too:
 * offline mode reads only the free-tier content subset, and a reading whose content gloss is
 * blanked is dropped from the build, so such a card cannot be shown here. The due counts do not
 * know that (they read the actor DB alone), so this is the one remaining way for a counted card
 * to go unserved. Joining on the reading's identity (`(grapheme, pinyin)`, not its gloss) keeps
 * it to genuinely missing content.
 *
 * Content is read in bounded, uncached batches. Passed-over batches go out of scope; even a long
 * search does not retain their data in the shared query cache.
 */
async function resolveFirstServableCard({
  candidates,
  isVoiceEligible,
  predictionCards,
  predictionQuizKeys,
  readUserCardAppCards,
}: {
  candidates: readonly ReviewEligibleDueUserCard[];
  isVoiceEligible?: (grapheme: string) => Promise<boolean>;
  predictionCards?: NextDueUserCardDataOverride["predictionCards"];
  predictionQuizKeys?: NextDueUserCardDataOverride["predictionQuizKeys"];
  readUserCardAppCards: ReturnType<typeof useReadUserCardAppCards>;
}): Promise<NextDueUserCardData | null> {
  for (let offset = 0; offset < candidates.length;) {
    // Usually the first candidate is valid. Do not hold it behind seven speculative content
    // lookups; only pay for batches when a missing/predicted/voice-ineligible card requires a search.
    const batchSize = offset === 0 ? 1 : CANDIDATE_RESOLVE_BATCH_SIZE;
    const batch = candidates.slice(offset, offset + batchSize);
    offset += batchSize;
    // One temporary actor/content batch, released before advancing to the next eight candidates.
    // The outer next-due query supplies the committed-write guard for the entire scan.
    const appCards = await readUserCardAppCards(batch.map((card) => card.userCardId));
    for (const [index, appCard] of appCards.entries()) {
      const candidate = batch[index];
      if (candidate === undefined) {
        continue;
      }
      const cardContent = resolveAppCardContent(appCard);
      if (cardContent === null) {
        continue;
      }
      if (cardContent.kind === "quiz") {
        // Voice study answers only meaning drills, and a quiz the speculative deck already holds
        // is passed over like any predicted card.
        if (isVoiceEligible !== undefined || predictionQuizKeys?.includes(cardContent.key)) {
          continue;
        }
        return {
          due: candidate.due,
          kind: "quiz",
          quizKey: cardContent.key,
          userCardId: candidate.userCardId,
        };
      }
      const drillType = cardContentDrillType(cardContent);
      const identityKey = cardContentIdentityKey(cardContent);
      if (
        predictionCards?.some(
          (card) =>
            cardIdentityKey({ drillType: card.drillType, subjectText: card.grapheme }) ===
            identityKey,
        )
      ) {
        continue;
      }
      if (
        isVoiceEligible &&
        (drillType.kind !== "meaning" ||
          !(await isVoiceEligible(cardContentSubject(cardContent).text)))
      ) {
        continue;
      }
      return {
        drillType,
        due: candidate.due,
        grapheme: cardContentSubject(cardContent).text,
        kind: "drill",
        userCardId: candidate.userCardId,
      };
    }
  }
  return null;
}
